import { useState, useEffect, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { extractErr } from '@/components/ui/inline-edit'

type Settings = {
  id: number
  provider: 'CLAUDE' | 'OPENAI' | 'GROK' | 'GEMINI' | 'OPENAI_COMPATIBLE'
  model: string
  base_url: string
  max_tokens: number
  is_enabled: boolean
  api_key_set: boolean
}

const PROVIDER_OPTIONS = [
  { value: 'CLAUDE',            label: 'Anthropic Claude' },
  { value: 'OPENAI',            label: 'OpenAI' },
  { value: 'GROK',              label: 'xAI Grok' },
  { value: 'GEMINI',            label: 'Google Gemini' },
  { value: 'OPENAI_COMPATIBLE', label: 'Other (OpenAI-compatible endpoint)' },
]

const DEFAULT_MODEL: Record<Settings['provider'], string> = {
  CLAUDE: 'claude-sonnet-4-6',
  OPENAI: 'gpt-4o-mini',
  GROK: 'grok-2-latest',
  GEMINI: 'gemini-1.5-pro',
  OPENAI_COMPATIBLE: '',
}

export default function AISettings() {
  const qc = useQueryClient()

  const settingsQ = useQuery({
    queryKey: ['ai-settings'],
    queryFn: async () => (await api.get<Settings>('/ai-settings/')).data,
  })

  const [form, setForm] = useState({
    provider: 'CLAUDE' as Settings['provider'],
    model: '',
    base_url: '',
    max_tokens: 4000,
    is_enabled: false,
    api_key: '',
  })

  useEffect(() => {
    if (settingsQ.data) {
      setForm((f) => ({
        ...f,
        provider: settingsQ.data!.provider,
        model: settingsQ.data!.model,
        base_url: settingsQ.data!.base_url,
        max_tokens: settingsQ.data!.max_tokens,
        is_enabled: settingsQ.data!.is_enabled,
        api_key: '', // never echo
      }))
    }
  }, [settingsQ.data])

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: any = {
        provider: form.provider,
        model: form.model,
        base_url: form.base_url,
        max_tokens: form.max_tokens,
        is_enabled: form.is_enabled,
      }
      // Only send api_key if user typed something — blank means "keep existing"
      if (form.api_key) payload.api_key = form.api_key
      return (await api.patch('/ai-settings/', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-settings'] })
      toast.success('AI settings saved')
      setForm((f) => ({ ...f, api_key: '' }))
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    saveMut.mutate()
  }

  const settings = settingsQ.data
  const showBaseUrl = form.provider === 'OPENAI_COMPATIBLE'

  return (
    <AdminLayout>
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground">AI Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure an external LLM for features like CV parsing. The API key is
          stored in the database in plain text — only users with the
          <code className="text-xs bg-muted px-1 mx-1 rounded">admin.ai_settings</code>
          permission can read or change it.
        </p>
      </div>

      {settingsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <form onSubmit={submit} className="bg-card rounded-lg shadow p-6 max-w-2xl">
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="provider">Provider</FieldLabel>
                <Select
                  value={form.provider}
                  onValueChange={(v) => {
                    const p = v as Settings['provider']
                    setForm({ ...form, provider: p, model: form.model || DEFAULT_MODEL[p] })
                  }}
                >
                  <SelectTrigger id="provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROVIDER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel htmlFor="model">Model</FieldLabel>
                <Input
                  id="model"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  placeholder={DEFAULT_MODEL[form.provider] || 'model-name'}
                  className="font-mono"
                />
              </Field>

              {showBaseUrl && (
                <Field>
                  <FieldLabel htmlFor="base-url">Base URL</FieldLabel>
                  <Input
                    id="base-url"
                    value={form.base_url}
                    onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                    placeholder="https://api.example.com/v1"
                    className="font-mono"
                  />
                </Field>
              )}

              <Field>
                <FieldLabel htmlFor="api-key">
                  API key {settings?.api_key_set && (
                    <span className="ml-2 text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                      key on file
                    </span>
                  )}
                </FieldLabel>
                <Input
                  id="api-key"
                  type="password"
                  value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  placeholder={settings?.api_key_set ? '••••••• (leave blank to keep existing)' : 'sk-…'}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Stored in plain text. Rotate regularly.
                </p>
              </Field>

              <Field>
                <FieldLabel htmlFor="max-tokens">Max tokens</FieldLabel>
                <Input
                  id="max-tokens"
                  type="number"
                  min={500}
                  max={64000}
                  value={form.max_tokens}
                  onChange={(e) => setForm({ ...form, max_tokens: Number(e.target.value || 4000) })}
                />
              </Field>

              <label className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  checked={form.is_enabled}
                  onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                />
                <span className="text-sm">
                  Enabled — features that call the LLM (CV parsing, etc.) are active when on.
                </span>
              </label>
            </FieldGroup>
          </FieldSet>

          <div className="pt-5 flex justify-end gap-2">
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}
    </AdminLayout>
  )
}
